import React, { type FC, type PropsWithChildren, useCallback, useEffect } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { AddRounded } from '@mui/icons-material'

import type { ArrayType } from '../../../utils'

import {
    ConnectwareError,
    selectIsLoading,
    selectUserManagementSelectedTabData,
    selectUserManagementSelectedTabPage,
    selectUserManagementSelectedTabSearch,
    type Translation,
    type UserManagementTabsTypes,
} from '../../../domain'

import { Table as BaseTable, type TableProps as BaseTableProps, Chip, type TableCustomizedState, type TableLine } from '../common'
import { ErrorMessage } from '../ErrorMessage'
import { useAppState, useAppUsecase } from '../State'
import { PermissionViewModal } from './Permissions'
import { RoleCreationModal, RoleEditModal } from './Roles'
import { UserCreationModal, UserEditModal } from './Users'

const errorStyle: SxProps<Theme> = { pt: 2 }

type TableProps<L extends TableLine,> = Readonly<{
    selected: keyof UserManagementTabsTypes
    notFound: Translation
    onAdd?: VoidFunction
    addLabel?: Translation
}> &
    Pick<BaseTableProps<L>, 'columns' | 'onRowClick'>

const Table = <L extends TableLine,>({ selected, notFound, columns, ...props }: PropsWithChildren<TableProps<L>>): ReturnType<FC> => {
    const data = useAppState((s) => selectUserManagementSelectedTabData(s, selected), [selected])
    const search = useAppState((s) => selectUserManagementSelectedTabSearch(s, selected), [selected])
    const page = useAppState((s) => selectUserManagementSelectedTabPage(s, selected), [selected])
    const isLoading = useAppState(selectIsLoading)
    const hasNoTableData = data === null

    const filterUsecase = useAppUsecase('filterUserManagementUsecase')

    const onCustomized = useCallback(
        (s: TableCustomizedState<L>) => {
            if (isLoading) {
                return
            }
            if ('page' in s) {
                filterUsecase.setPage(selected, s.page + 1)
            }
            if ('search' in s) {
                filterUsecase.search(selected, s.search)
            }
        },
        [filterUsecase, selected, isLoading]
    )

    /**
     * @todo look into how to render this component differently
     * @see https://cybusio.atlassian.net/browse/CC-3276
     */
    if (ConnectwareError.is(data)) {
        return <ErrorMessage sx={errorStyle} data-testid="users-management-error" error={data} stack extras="section" />
    }

    const [onAdd, addLabel, onRowClick] = isLoading ? [] : [props.onAdd, props.addLabel, props.onRowClick]

    return (
        <BaseTable
            columns={columns}
            data={hasNoTableData ? [] : (data.current as (L & ArrayType<typeof data.current>)[])}
            translations={{ emptyTable: notFound }}
            extendedToolbar={onAdd && addLabel && <Chip data-testid="add-button" label={addLabel} avatar={AddRounded} onClick={onAdd} />}
            pagination={hasNoTableData ? undefined : { pageSize: data.pageSize, pageSizeOptions: [data.pageSize], page: page - 1, totalCount: data.totalCount }}
            onRowClick={onRowClick}
            search={search === null || search}
            loading={hasNoTableData}
            onCustomized={onCustomized}
        />
    )
}

export const Page = <L extends TableLine,>({ children, ...props }: PropsWithChildren<TableProps<L>>): ReturnType<FC> => {
    const loadUsecase = useAppUsecase('loadUserManagementDataUsecase')
    useEffect(() => loadUsecase.invoke(props.selected), [loadUsecase, props.selected])

    return (
        <Box data-testid="management-table">
            <Table {...props} />

            {/* The modals need to be here in order to be shown in every page */}
            <UserEditModal />
            <UserCreationModal />
            <RoleEditModal />
            <RoleCreationModal />
            <PermissionViewModal />
        </Box>
    )
}
